import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './teams.module.css';

import constants from '../../../constants.js';
import api from '../../../routes/api.js';
import utils from '../../../utils/utils.js';

import { useSidebarProvider } from '../../../providers/SidebarProvider.js';
import { useApiProvider } from '../../../providers/ApiProvider.js';
import { useLeagueTypeProvider } from '../../../providers/LeagueTypeProvider';

import { ReactComponent as Location } from '../../../assets/location.svg';
import { ReactComponent as Website } from '../../../assets/website.svg';
import { ReactComponent as Phone } from '../../../assets/teamphone.svg';
import { ReactComponent as Facebook } from '../../../assets/facebook.svg';
import { ReactComponent as Instagram } from '../../../assets/instagram.svg';
import { ReactComponent as Youtube } from '../../../assets/youtube.svg';

import InfoDialog from '../../common/InfoDialog/InfoDialog.js';
import Select from "../../common/Select/Select.js";
import Table from "../../common/Table/Table.js";

import { IconTrophy } from '@tabler/icons-react';
import { IconMedal } from '@tabler/icons-react';
import { IconLayoutGrid } from '@tabler/icons-react';


const Teams = ({selectedLocation, selectedDivision, setSelectedMenuItem}) => {
  const navigate = useNavigate();

  const { setIsSidebarShowing } = useSidebarProvider();
  const { setDialog, isLoading, setIsLoading, LoadingSpinner} = useApiProvider();
  const { leagueType } = useLeagueTypeProvider();

  const [currentPage, setCurrentPage] = useState(constants.teamsPages[0]);
  const [ allTeams, setAllTeams ] = useState([]);
  const [ selectedTeam, setSelectedTeam ] = useState(null);
  const [ players, setPlayers ] = useState([]);
  const [ schedule, setSchedule ] = useState([]);
  const [infoDialogText, setInfoDialogText] = useState({
    title: "",
    body: ""
  });

  const infoDialogRef = useRef(null);

  const openInfoDialog = () => {
    if (infoDialogRef.current){
      infoDialogRef.current.style.display = 'flex';
      infoDialogRef.current.showModal();
    }
  }

  const closeInfoDialog = () => {
    if (infoDialogRef.current){
      infoDialogRef.current.style.display = 'none';
      infoDialogRef.current.close();
    }
  }

  const getTeamsForDropdown = () => {
    let options = [];

    allTeams.forEach((t) => {
      let option = {};
      option.key = t[constants.teamId];
      option.value = t[constants.teamId];
      option.label = t[constants.teamName];

      options.push(option);
    })

    return options;
  }

  const goToMatchup = (location, division, matchup, navigate) => {
    if (matchup !== null) {navigate('/' + leagueType + '/matchup', { 
      replace: true,
      state: {
        selectedLocation: location,
        selectedDivision: division, 
        matchup: {
          matchupId: matchup[constants.matchupId],
          matchupDate: matchup[constants.date],
          homeClub: matchup[constants.homeClub],
          homeTeamName: matchup[constants.homeName].toUpperCase(),
          homeTeamId: matchup[constants.homeTeamId],
          awayClub: matchup[constants.awayClub],
          awayTeamName: matchup[constants.awayName].toUpperCase(),
          awayTeamId: matchup[constants.awayTeamId],
        }
      }
    })}
}

  const formatScheduleDataForTable = (schedule, teamName) => {
    if (!schedule || schedule.length === 0) {
      return [];
    }

    const formattedData = schedule.map((m) => {
      m[constants.date] = new Date(m[constants.scheduledTime]).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric'
      }).replace(',', ' -');

      m[constants.time] = new Date(m[constants.scheduledTime]).toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }).replace(',', ' -');

      const isHomeTeam = teamName === m[constants.homeName];
      const opponentTeamName = isHomeTeam ? m[constants.awayName] : m[constants.homeName];

      m[constants.opponent] = 
        <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:'10px'}}>
          <div>{isHomeTeam ? "VS " : "@ "}</div>
          <img 
            src={`/logos/${teamName == m[constants.homeName] ? m[constants.awayClub][constants.logo] : m[constants.homeClub][constants.logo]}.png`} 
            style={{height:'32px', width: '32px'}}
          />
          {opponentTeamName}
        </div>;

      m[constants.wins] = teamName == m[constants.homeName] ? m[constants.homeWins] : m[constants.awayWins];
      m[constants.losses] = teamName == m[constants.homeName] ? m[constants.awayWins] : m[constants.homeWins];

      m[constants.api.address] = m[constants.api.address] ?? "";
      m[constants.location] = isHomeTeam ? constants.labels.home : constants.labels.away;

      m[constants.scorecard] = 
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <a 
            onClick={() => {goToMatchup(selectedLocation, selectedDivision, m, navigate)}} 
            style={{fontSize: '1rem', cursor: 'pointer'}}
          >
            View Matchup
          </a>
        </div>;

      return m;
    });

    return formattedData.sort((a, b) => 
      new Date(a.scheduledTime) - new Date(b.scheduledTime)
    );
  }

  const onTeamChange = (e) => {
    const newTeam = allTeams.find(t => t[constants.teamId] == e.currentTarget.value);

    if (!newTeam) return;

    const newPlayers = (newTeam[constants.teamPlayerStats]?.[constants.values] ?? []).map(p => {
      p[constants.fullName] = p[constants.firstName] + " " + p[constants.lastName];
      return p;
    });

    const newSchedule = newTeam[constants.schedule]?.[constants.values] ?? [];

    setSelectedTeam(newTeam);
    setPlayers(newPlayers);
    setSchedule(formatScheduleDataForTable(newSchedule, newTeam[constants.teamName]));
  }

  const changePage = (e) => {
    setCurrentPage(e.currentTarget.innerHTML);
  }

  useEffect(() => {
    if (isLoading) return; // Wait for data from App.js
    
    const getData = async () => {
      setIsLoading(true);
      try {
        const baseUrl = utils.getBaseUrl(leagueType);

        const teamsResponse = await api.fetch(baseUrl, constants.uri.teams(selectedDivision[constants.divisionId]));   
        if (teamsResponse.hasOwnProperty('errors')) throw teamsResponse;

        const teams = teamsResponse[constants.values] ?? [];

        if (teams.length === 0) {
          setAllTeams([]);
          setSelectedTeam(null);
          setPlayers([]);
          setSchedule([]);
          setIsLoading(false);
          return;
        }

        const firstTeam = teams[0];

        const players = (firstTeam[constants.teamPlayerStats]?.[constants.values] ?? []).map(p => {
          p[constants.fullName] = 
          <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start', alignItems:'center', gap:'10px'}}>
            {p[constants.firstName] + " " + p[constants.lastName]}
            {utils.getPlayerStatusIcon(p)}
          </div>
          return p;
        });

        const firstTeamSchedule = firstTeam[constants.schedule]?.[constants.values] ?? [];

        setAllTeams(teams);
        setSelectedTeam(firstTeam);
        setPlayers(players);
        setSchedule(formatScheduleDataForTable(firstTeamSchedule, firstTeam[constants.teamName]));
        setIsLoading(false);
      }
      catch(err) {
        console.error(err);
        setDialog({
          title: err.title ? err.title : constants.messages.errorMessage, 
          code: err.code ? err.code : "N/A", 
          message: err.message, 
          isError: true});
        setIsLoading(false);
      }
    }

    //TODO: Why is this triggering before the selectedDivision is set
    if (selectedDivision[constants.divisionId] != "") getData();

    // If the page is loading on a small width, hide the sidebar so you see the page
    if (window.innerWidth < 1300){
      setIsSidebarShowing(false);
    }

    setSelectedMenuItem(constants.sidebarItems[5]);
  }, [selectedDivision]);

  return (
    selectedTeam ? <div className={styles.Teams}>

      <InfoDialog ref={infoDialogRef}
                dialogTitle={infoDialogText.title}
                dialogMessage={infoDialogText.body}
                closeDialog={closeInfoDialog}/>

      <div className={styles.TeamSelectContainer}>
        <Select
          disabled={false}
          onChange={onTeamChange}
          currentValue={selectedTeam[constants.teamId]}
          options={getTeamsForDropdown()}
          color={selectedTeam[constants.api.club][constants.api.color]}
          additionalStyle={`${styles.TeamSelect}`}/>
      </div>

      <hr style={{color: `${selectedTeam[constants.api.club][constants.api.color]}`}} className={styles.Spacer}/>

      <div className={styles.Header}>

        <div style={{display: 'flex', flexWrap: 'nowrap', justifyContent: 'center', alignItems: 'center'}}>
          <img style={{height: '4em', width: 'auto'}} src={`/logos/${selectedTeam[constants.api.club][constants.logo]}.png`}/>
          <div style={{color: `${selectedTeam[constants.api.club][constants.api.color]}`, fontWeight: '700', fontSize: '2rem'}}>{selectedTeam[constants.teamName]}</div> 
        </div>

        <div className={styles.SubTitle}> {`${selectedLocation[constants.locationName] ? selectedLocation[constants.locationName] : "location"} ${selectedDivision[constants.divisionName] ? selectedDivision[constants.divisionName] : "division"}`}</div>
       
        <div className={styles.Main}>
          <Location 
            onClick={() => {window.open(`https://www.google.com/maps/search/?api=1&query=${selectedTeam[constants.api.club][constants.api.address]}`, '_blank')}} 
            style={{cursor: 'pointer', color: `${selectedTeam[constants.api.club][constants.api.color]}`, display: selectedTeam[constants.api.club][constants.api.address] ? "block" : "none"}}/>
          <Website 
            onClick={() => {window.open(`${selectedTeam[constants.api.club][constants.api.website]}`, '_blank', 'noopener,noreferrer')}} 
            style={{cursor: 'pointer', color: `${selectedTeam[constants.api.club][constants.api.color]}`, display: selectedTeam[constants.api.club][constants.api.website] ? "block" : "none"}}/>
          <Phone 
            onClick={() => {window.open(`tel:${selectedTeam[constants.api.club][constants.api.phone]}`, '_blank', 'noopener,noreferrer')}} 
            style={{cursor: 'pointer', color: `${selectedTeam[constants.api.club][constants.api.color]}`, display: selectedTeam[constants.api.club][constants.api.phone] ? "block" : "none"}}/>
          <Facebook 
            onClick={() => {window.open(`${selectedTeam[constants.api.club][constants.api.facebook]}`, '_blank', 'noopener,noreferrer')}} 
            style={{cursor: 'pointer', color: `${selectedTeam[constants.api.club][constants.api.color]}`, display: selectedTeam[constants.api.club][constants.api.facebook] ? "block" : "none"}}/>
          <Instagram 
            onClick={() => {window.open(`${selectedTeam[constants.api.club][constants.api.instagram]}`, '_blank', 'noopener,noreferrer')}} 
            style={{cursor: 'pointer', color: `${selectedTeam[constants.api.club][constants.api.color]}`, display: selectedTeam[constants.api.club][constants.api.instagram] ? "block" : "none"}}/>
          <Youtube 
            onClick={() => {window.open(`${selectedTeam[constants.api.club][constants.api.youtube]}`, '_blank', 'noopener,noreferrer')}} 
            style={{cursor: 'pointer', color: `${selectedTeam[constants.api.club][constants.api.color]}`, display: selectedTeam[constants.api.club][constants.api.youtube] ? "block" : "none"}}/>
        </div>
      </div>
      
      <div className={styles.Display}>

        <hr style={{color: `${selectedTeam[constants.api.club][constants.api.color]}`}} className={styles.Spacer}/>

        <div className={styles.ButtonGroup}>
          <button
            style={{
              color: currentPage == constants.teamsPages[0] ? constants.colors.cplWhite :`${selectedTeam[constants.api.club][constants.api.color]}`,
              border: `1px solid ${selectedTeam[constants.api.club][constants.api.color]}`,
              backgroundColor: currentPage == constants.teamsPages[0] ? `${selectedTeam[constants.api.club][constants.api.color]}` : constants.colors.cplWhite
            }}
            onClick={changePage}
            className={styles.NavButton}> 
              {constants.teamsPages[0]}
          </button>
          
          <button
            style={{
              color: currentPage == constants.teamsPages[1] ? constants.colors.cplWhite :`${selectedTeam[constants.api.club][constants.api.color]}`,
              border: `1px solid ${selectedTeam[constants.api.club][constants.api.color]}`,
              backgroundColor: currentPage == constants.teamsPages[1] ? `${selectedTeam[constants.api.club][constants.api.color]}` : constants.colors.cplWhite
            }}
            onClick={changePage}
            className={styles.NavButton}>
              {constants.teamsPages[1]}
          </button>

          <button
            style={{
              color: currentPage == constants.teamsPages[2] ? constants.colors.cplWhite :`${selectedTeam[constants.api.club][constants.api.color]}`,
              border: `1px solid ${selectedTeam[constants.api.club][constants.api.color]}`,
              backgroundColor: currentPage == constants.teamsPages[2] ? `${selectedTeam[constants.api.club][constants.api.color]}` : constants.colors.cplWhite
            }}
            onClick={changePage}
            className={styles.NavButton}>
              {constants.teamsPages[2]}
          </button>
        </div>

        <hr style={{color: `${selectedTeam[constants.api.club][constants.api.color]}`}} className={styles.Spacer}/>
        
          {currentPage == constants.teamsPages[0] ? <TeamOverview teamStats={selectedTeam[constants.statistics]} teamColor={selectedTeam[constants.api.club][constants.api.color]} leagueType={leagueType}/> : null}
          {currentPage == constants.teamsPages[1] ? <PlayerStats players={players} setPlayers={setPlayers} teamColor={selectedTeam[constants.api.club][constants.api.color]} setInfoDialogText={setInfoDialogText} openInfoDialog={openInfoDialog} leagueType={leagueType}/> : null}
          {currentPage == constants.teamsPages[2] ? <TeamSchedule schedule={schedule} setSchedule={setSchedule} teamColor={selectedTeam[constants.api.club][constants.api.color]} leagueType={leagueType}/> : null}

      </div> 

    </div> : <LoadingSpinner/>
  )
}

const TeamOverview = ({teamStats, teamColor, leagueType}) => {
  function getWinPercentage(wins, losses) {
    const totalGames = wins + losses;
    return totalGames === 0 ? '—' : `${((wins / totalGames) * 100).toFixed(1)}%`;
  }
  return (
    <div className={styles.Content}>
        <div style={{color: `${teamColor}`}} className={styles.ContentHeader}>{constants.labels.standing}</div>

        <div className={styles.StatsRow}>
          <div style={{border: `1px solid ${teamColor}`}} className={styles.StatsBox}>
            <div className={styles.TeamStatsHeader}>
              <IconTrophy className="standing-icon" color={teamColor} /> 
              <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.record}</div>
            </div>
            <div style={{color: `${teamColor}`}} className={styles.TeamStatsText}>
              {teamStats[constants.api.record]}
            </div>
          </div>

          <div style={{border: `1px solid ${teamColor}`}} className={styles.StatsBox}>
            <div className={styles.TeamStatsHeader}>
              <IconMedal  className="standing-icon" color={teamColor} /> 
              <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.place}</div>
            </div>
            <div style={{color: `${teamColor}`}} className={styles.TeamStatsText}>
              {teamStats[constants.api.ranking]}
            </div>
          </div>

          <div style={{border: `1px solid ${teamColor}`}} className={styles.StatsBox}>
            <div className={styles.TeamStatsHeader}>
              <IconLayoutGrid   className="standing-icon" color={teamColor} /> 
              <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.labels.podRanking}</div>
            </div>
            <div style={{color: `${teamColor}`}} className={styles.TeamStatsText}>
              {teamStats[constants.api.podRanking]}
            </div>
          </div>
        </div>

        <div className={styles.StatsRow}>
          <div style={{border: `1px solid ${teamColor}`}} className={styles.StatsBox}>
            <div className={styles.TeamStatsHeader}>
              <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.labels.totalPointsWon}</div>
            </div>
            <div style={{color: `${teamColor}`}} className={styles.TeamStatsText}>
              {teamStats[constants.api.totalPointsWon]}
            </div>
          </div>

          <div style={{border: `1px solid ${teamColor}`}} className={styles.StatsBox}>
            <div className={styles.TeamStatsHeader}>
              <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.labels.averagePPG}</div>
            </div>
            <div style={{color: `${teamColor}`}} className={styles.TeamStatsText}>
              {teamStats[constants.api.averagePointsPerGame]}
            </div>
          </div>

          <div style={{border: `1px solid ${teamColor}`}} className={styles.StatsBox}>
            <div className={styles.TeamStatsHeader}>
              <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.labels.pointDifferential}</div>
            </div>
            <div style={{color: `${teamColor}`}} className={styles.TeamStatsText}>
              {teamStats[constants.api.averagePointDifferential]}
            </div>
          </div>

          <div style={{border: `1px solid ${teamColor}`}} className={styles.StatsBox}>
            <div className={styles.TeamStatsHeader}>
              <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.labels.gameRecord}</div>
            </div>
            <div style={{color: `${teamColor}`}} className={styles.TeamStatsText}>
              {teamStats[constants.api.gameRecord]}
            </div>
          </div>
        </div>

        <div className={styles.StatsContainer} style={{border: `1px solid ${teamColor}`}}>
          <div className={styles.TeamStatsHeader}>
            <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.titles.homeVsAway}</div>
          </div>
          <div className={styles.ComparisonGrid}>
            <div className={styles.StatsGrid}>
              <div className={styles.TeamStatsHeader} style={{color: `${teamColor}`, borderBottom: `1px solid ${teamColor}`, paddingBottom:'1em'}}>{constants.labels.home}</div>
              <div className={styles.SideInner}>
                <div className={styles.SideStat}>
                  <div className={styles.SideStatLabel} style={{color: `${teamColor}`}}>{constants.labels.match}</div>
                  <div className={styles.TeamStatsText} style={{color: `${teamColor}`}}>{teamStats[constants.api.homeRecord]}</div>
                  <div className={styles.SideStatSub} style={{color: `${teamColor}`}}>{`${teamStats[constants.api.homeWinRate]}%`}</div>
                </div>
                <div className={styles.SideDivider} style={{backgroundColor: `${teamColor}`}}/>
                <div className={styles.SideStat}>
                  <div className={styles.SideStatLabel} style={{color: `${teamColor}`}}>{constants.labels.game}</div>
                  <div className={styles.TeamStatsText}  style={{color: `${teamColor}`}}>{teamStats[constants.api.homeGameWins]}-{teamStats[constants.api.homeGameLosses]}</div>
                  <div className={styles.SideStatSub} style={{color: `${teamColor}`}}>{getWinPercentage(teamStats[constants.api.homeGameWins],teamStats[constants.api.homeGameLosses])}</div>
                </div>
              </div>
            </div>
            <div className={styles.StatsGrid}>
              <div className={styles.TeamStatsHeader} style={{color: `${teamColor}`, borderBottom: `1px solid ${teamColor}`, paddingBottom:'1em'}}>{constants.labels.away}</div>
              <div className={styles.SideInner}>
                <div className={styles.SideStat}>
                  <div className={styles.SideStatLabel} style={{color: `${teamColor}`}}>{constants.labels.match}</div>
                  <div className={styles.TeamStatsText} style={{color: `${teamColor}`}}>{teamStats[constants.api.awayRecord]}</div>
                  <div className={styles.SideStatSub} style={{color: `${teamColor}`}}>{`${teamStats[constants.api.awayWinRate]}%`}</div>
                </div>
                <div className={styles.SideDivider} style={{backgroundColor: `${teamColor}`}}/>
                <div className={styles.SideStat}>
                  <div className={styles.SideStatLabel} style={{color: `${teamColor}`}}>{constants.labels.game}</div>
                  <div className={styles.TeamStatsText}  style={{color: `${teamColor}`}}>{teamStats[constants.api.awayGameWins]}-{teamStats[constants.api.awayGameLosses]}</div>
                  <div className={styles.SideStatSub} style={{color: `${teamColor}`}}>{getWinPercentage(teamStats[constants.api.awayGameWins],teamStats[constants.api.awayGameLosses])}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {leagueType !== constants.pages.genderLeagues ? <div className={styles.StatsContainer} style={{border: `1px solid ${teamColor}`}}>
          <div className={styles.TeamStatsHeader}>
            <div style={{color: `${teamColor}`, fontWeight: '600'}}>{constants.titles.winRateByFormat}</div>
          </div>
          <div className={styles.FormatGrid}>
            <div className={styles.StatsCard}>
              <div className={styles.TeamStatsHeader} style={{color: `${teamColor}`, borderBottom: `1px solid ${teamColor}`, paddingBottom:'1em'}}>{constants.labels.womens}</div>
                <div className={styles.StatColumn}>
                  <div className={styles.SideStatLabel} style={{color: `${teamColor}`}}>{constants.labels.record}</div>
                  <div className={styles.TeamStatsText} style={{color: `${teamColor}`}}>{teamStats[constants.api.womensRecord]}</div>
                  <div className={styles.BarContainer}>
                    <div className={styles.BarTrack}> <div className={styles.BarFill} style={{backgroundColor: `${teamColor}`, width: `${teamStats[constants.api.womenWinRate]}%`}}/> </div>
                    <div className={styles.SideStatSub} style={{color: `${teamColor}`}}>{`${teamStats[constants.api.womenWinRate]}%`}</div>
                  </div>
                </div>
            </div>
            <div className={styles.StatsCard}>
              <div className={styles.TeamStatsHeader} style={{color: `${teamColor}`, borderBottom: `1px solid ${teamColor}`, paddingBottom:'1em'}}>{constants.labels.mixed}</div>
                <div className={styles.StatColumn}>
                  <div className={styles.SideStatLabel} style={{color: `${teamColor}`}}>{constants.labels.record}</div>
                  <div className={styles.TeamStatsText} style={{color: `${teamColor}`}}>{teamStats[constants.api.mixedRecord]}</div>
                  <div className={styles.BarContainer}>
                    <div className={styles.BarTrack}> <div className={styles.BarFill} style={{backgroundColor: `${teamColor}`, width: `${teamStats[constants.api.mixedWinRate]}%`}}/> </div>
                    <div className={styles.SideStatSub} style={{color: `${teamColor}`}}>{`${teamStats[constants.api.mixedWinRate]}%`}</div>
                  </div>
                </div>
            </div>
            <div className={styles.StatsCard}>
              <div className={styles.TeamStatsHeader} style={{color: `${teamColor}`, borderBottom: `1px solid ${teamColor}`, paddingBottom:'1em'}}>{constants.labels.mens}</div>
                <div className={styles.StatColumn}>
                  <div className={styles.SideStatLabel} style={{color: `${teamColor}`}}>{constants.labels.record}</div>
                  <div className={styles.TeamStatsText} style={{color: `${teamColor}`}}>{teamStats[constants.api.mensRecord]}</div>
                  <div className={styles.BarContainer}>
                    <div className={styles.BarTrack}> <div className={styles.BarFill} style={{backgroundColor: `${teamColor}`, width: `${teamStats[constants.api.menWinRate]}%` }}/> </div>
                    <div className={styles.SideStatSub} style={{color: `${teamColor}`}}>{`${teamStats[constants.api.menWinRate]}%`}</div>
                  </div>
                </div>
            </div>
          </div>
        </div> : <></>}


    </div>
  )
}

const PlayerStats = ({players, setPlayers, teamColor, setInfoDialogText, openInfoDialog}) => {
  return (
    <div className={styles.Content}>
      <div style={{color: `${teamColor}`}} className={styles.ContentHeader}>{constants.teamsPages[1]}</div>
        <Table
          headers={constants.table.genderResultCols(setInfoDialogText, openInfoDialog)}
          headerColor={teamColor}
          defaultHeader={constants.ranking}
          tableKeys={[constants.playerId]}
          tableData={players}
          setTableData={setPlayers}
          canSort={true}/>
    </div>
  )
}

const TeamSchedule = ({schedule, setSchedule, teamColor}) => {
  return (
    <div className={styles.Content}>
      <div style={{color: `${teamColor}`}} className={styles.ContentHeader}>{constants.teamsPages[2]}</div>
        <Table
          headers={constants.table.teamScheduleCols}
          headerColor={teamColor}
          defaultHeader={constants.weekNumber}
          tableKeys={[constants.matchupId]}
          tableData={schedule}
          setTableData={setSchedule}
          canSort={true}/>
    </div>
  )
}
export default Teams;